import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { signOut as signOutFromMsal } from '../auth/msal';
import { useSession } from '../session';
import { BRAND_LOGO_SRC, BRAND_NAME } from '../branding';
import { Button } from './ui';

type NavItem = {
  label: string;
  to: string;
};

type UnifiedHeaderProps = {
  showNav?: boolean;
};

const navBaseClass =
  'inline-flex items-center justify-center h-10 px-4 rounded-lg text-[13px] font-bold tracking-[0.01em]';
const navActiveClass = `${navBaseClass} bg-primary/10 text-primary border border-primary/10`;
const navInactiveClass =
  `${navBaseClass} border border-border bg-white text-ink transition hover:bg-slate-50 hover:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/35`;

const opsActiveClass = `${navBaseClass} bg-amber-100 text-amber-950 border border-amber-200`;
const opsInactiveClass =
  `${navBaseClass} border border-amber-400 bg-amber-50 text-amber-950 transition hover:bg-amber-100 hover:border-amber-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/35`;

export default function UnifiedHeader({ showNav = true }: UnifiedHeaderProps) {
  const location = useLocation();
  const { profile, isAppEligible, isSystemAdmin } = useSession();
  const shouldShowNav = showNav && isAppEligible;
  const navItems = React.useMemo<NavItem[]>(() => {
    const items: NavItem[] = [
      { label: 'Overview', to: '/app' },
      { label: 'Calls', to: '/calls' },
      { label: 'Settings', to: '/settings' },
    ];
    if (isSystemAdmin) {
      items.push({ label: 'Ops', to: '/ops' });
    }
    return items;
  }, [isSystemAdmin]);

  async function handleSignOut() {
    await signOutFromMsal();
  }

  return (
    <header className="sticky top-0 z-10 border-b border-border/90 bg-surfaceAlt/90 backdrop-blur-md">
      <div className="px-4 sm:px-6 lg:px-8 py-3">
        <div className="grid grid-cols-[auto,1fr,auto] items-center gap-4">
          <div className="flex items-center gap-3">
            <img
              src={BRAND_LOGO_SRC}
              alt={BRAND_NAME}
              className="w-10 h-10 object-contain"
            />
            <div className="flex flex-col leading-tight">
              <span className="text-[18px] font-extrabold tracking-[-0.01em]">{BRAND_NAME}</span>
              {profile?.name && (
                <span className="text-[12px] text-muted-foreground">
                  Signed in as
                  {' '}
                  {profile.name}
                </span>
              )}
            </div>
          </div>
          {shouldShowNav ? (
            <nav className="flex items-center justify-center gap-2" aria-label="Primary">
              {navItems.map((item) => {
                const isOpsItem = item.to === '/ops';
                const isExactMatch = location.pathname === item.to;
                const isOpsSubpage = isOpsItem && location.pathname.startsWith('/ops/');
                const isActiveItem = isExactMatch || isOpsSubpage;
                if (isActiveItem) {
                  if (isOpsSubpage) {
                    return (
                      <Link
                        key={item.to}
                        to={item.to}
                        className={opsActiveClass}
                      >
                        {item.label}
                      </Link>
                    );
                  }
                  return (
                    <span
                      key={item.to}
                      className={isOpsItem ? opsActiveClass : navActiveClass}
                      aria-current="page"
                    >
                      {item.label}
                    </span>
                  );
                }
                return (
                  <Link key={item.to} to={item.to} className={isOpsItem ? opsInactiveClass : navInactiveClass}>
                    {item.label}
                  </Link>
                );
              })}
            </nav>
          ) : (
            <div aria-hidden />
          )}
          <div className="flex items-center justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              className="h-10 px-4 gap-2 font-bold"
              onClick={handleSignOut}
            >
              <svg aria-hidden className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <path d="M16 17l5-5-5-5" />
                <path d="M21 12H9" />
              </svg>
              Sign out
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
}
